'use client';
/**
 * Paramétrage › LECTURE DU CATALOGUE — qui voit quoi (RQ, 14/09/2026).
 *
 * « Le catalogue choisit ce qu'il affiche selon la personne qui consulte
 *  (préleveur, laboratoire, prescripteur). »
 *
 * Deux réglages, écrits ensemble dans `field-config.lectureCatalogue` :
 *   · une grille rubrique × lecteur — chaque rubrique d'un point d'entrée
 *     (consigne, conservation, critères…) et les lecteurs qui la voient ;
 *   · les règles communes, dites UNE fois au-dessus des points d'entrée
 *     (l'identification à deux points, retirée des profils le 14/09/2026).
 *
 * Quels POINTS D'ENTRÉE chaque lecteur voit se règle sur l'examen lui-même
 * (Référentiel › examen › Transport & réception › « Vu par »).
 *
 * Tant que rien n'est enregistré, l'écran part de `LECTURE_PAR_DEFAUT`, qui
 * reproduit l'affichage d'avant la bascule. Réservé à la RQ, contrôlé par le
 * moteur (`erreurLectureCatalogue`), tracé au journal `MAN-PARAMETRAGE`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { card, inputStyle } from '@/components/ui';
import { moteur } from './Registres';
import {
  LECTEURS_CATALOGUE, RUBRIQUES_ENTREE, lectureEnVigueur, erreurLecture,
  type LectureCatalogue, type LecteurCatalogue, type RubriqueEntree,
} from '@/shared/lectureCatalogue';

import { Bouton } from '@/shared/Bouton';
import { enTeteCollant } from '@/shared/Tableau';
const cellule: React.CSSProperties = { padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', borderBottom: '1px solid var(--border-subtle)', fontSize: 'var(--tableau-ligne-police)' };

export function OngletLectureCatalogue({ estRq, parametres }: {
  estRq: boolean;
  /** `field-config.lectureCatalogue` tel que lu — `null` tant que rien n'est enregistré. */
  parametres: unknown;
}) {
  const [lecture, setLecture] = useState<LectureCatalogue>(() => lectureEnVigueur(parametres));
  const [enregistre, setEnregistre] = useState(!!parametres);
  const [modifie, setModifie] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  /* LA VERSION LUE DE `lectureCatalogue` (16/10/2026), renvoyée sous `versions`
     à l'écriture : la clé est remplacée ENTIÈRE, et cette grille part de ce que
     l'onglet a lu. Si un autre l'a changée entre-temps, le moteur refuse (409)
     au lieu d'écraser — même règle que `Parametrage.tsx`. L'onglet relit le
     paramétrage à son ouverture pour que la valeur et sa version viennent de
     la MÊME lecture (le parent a lu plus tôt) ; la grille n'est remplacée que
     si rien n'y a encore été touché. `null` = moteur sans versions : on n'en
     envoie pas. */
  const versionLue = useRef<string | null>(null);
  const modifieRef = useRef(false);
  const [relecture, setRelecture] = useState(0);
  useEffect(() => {
    let vivant = true;
    void moteur('manuel-ref/parametrage').then((r) => {
      if (!vivant || !r.ok) return;
      const v = (r.data.versions as Record<string, unknown> | undefined)?.lectureCatalogue;
      versionLue.current = typeof v === 'string' ? v : null;
      const lu = (r.data.parametrage as Record<string, unknown> | null)?.lectureCatalogue ?? null;
      // Après un refus (relecture > 0), le brouillon périmé est remplacé d'office.
      if (relecture > 0 || !modifieRef.current) {
        setLecture(lectureEnVigueur(lu)); setEnregistre(!!lu); setModifie(false); modifieRef.current = false;
      }
    });
    return () => { vivant = false; };
  }, [relecture]);

  const changer = (l: LectureCatalogue) => { setLecture(l); setModifie(true); modifieRef.current = true; setMessage(''); setErreur(''); };
  const basculer = (r: RubriqueEntree, lecteur: LecteurCatalogue) => {
    const actuels = lecture.visibilite[r] || [];
    changer({ ...lecture, visibilite: { ...lecture.visibilite, [r]: actuels.includes(lecteur) ? actuels.filter((x) => x !== lecteur) : [...actuels, lecteur] } });
  };
  const changerRegle = (id: string, patch: Partial<LectureCatalogue['reglesCommunes'][number]>) =>
    changer({ ...lecture, reglesCommunes: lecture.reglesCommunes.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const ajouterRegle = () => {
    let n = lecture.reglesCommunes.length + 1;
    while (lecture.reglesCommunes.some((x) => x.id === `regle-${n}`)) n += 1;
    changer({ ...lecture, reglesCommunes: [...lecture.reglesCommunes, { id: `regle-${n}`, texte: '', lecteurs: ['preleveur', 'labo'] }] });
  };
  const enregistrer = () => {
    const e = erreurLecture(lecture);
    if (e) { setErreur(e); return; }
    setEnvoi(true); setErreur(''); setMessage('');
    const corps: Record<string, unknown> = { lectureCatalogue: lecture };
    if (versionLue.current) corps.versions = { lectureCatalogue: versionLue.current };
    void moteur('manuel-ref/parametrage', corps).then((r) => {
      setEnvoi(false);
      if (r.status === 409) {
        /* Quelqu'un a changé la grille depuis la lecture : rien n'est écrit.
           On relit (valeur et version) pour que la RQ voie sa version avant
           de refaire son geste. */
        setErreur(r.message);
        setRelecture((n) => n + 1);
        return;
      }
      if (!r.ok) { setErreur(r.message); return; }
      const n = (r.data.versions as Record<string, unknown> | undefined)?.lectureCatalogue;
      if (typeof n === 'string') versionLue.current = n;
      setEnregistre(true); setModifie(false); modifieRef.current = false;
      setMessage('Réglage enregistré — le catalogue l’appliquera à son prochain chargement.');
    });
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ ...card, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Lecture du catalogue</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Ce que chaque lecteur voit d’un point d’entrée (matière × état) sur la fiche publique. Les textes viennent des
          profils de matériel et des points d’entrée de chaque examen ; ici, on choisit seulement <strong>qui les lit</strong>.
          Quels points d’entrée chaque lecteur voit se règle sur l’examen (« Vu par »).
        </div>
        {!enregistre && (
          <div role="status" style={{ marginTop: 8, padding: '8px 11px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50)', border: '1px solid #EBD9B4', fontSize: 12.5, color: 'var(--amber-700)' }}>
            Rien n’est encore enregistré : cette grille reproduit l’affichage actuel du catalogue.
          </div>
        )}
      </div>

      {message && <div style={{ color: 'var(--green-600)', fontSize: 12.5 }}>{message}</div>}
      {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th scope="col" style={{ ...cellule, textAlign: 'start', fontSize: 'var(--tableau-entete-police)', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Rubrique</th>
              {LECTEURS_CATALOGUE.map((l) => <th scope="col" key={l.code} style={{ ...cellule, fontSize: 'var(--tableau-entete-police)', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{l.court}</th>)}
            </tr>
          </thead>
          <tbody>
            {RUBRIQUES_ENTREE.map((r) => (
              <tr key={r.code}>
                <td style={cellule}>
                  <div style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{r.libelle}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Champ : {r.source}</div>
                </td>
                {LECTEURS_CATALOGUE.map((l) => (
                  <td key={l.code} style={{ ...cellule, textAlign: 'center' }}>
                    <input type="checkbox" disabled={!estRq} checked={(lecture.visibilite[r.code] || []).includes(l.code)}
                      aria-label={`${l.court} voit « ${r.libelle} »`} onChange={() => basculer(r.code, l.code)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...card, padding: 12, display: 'grid', gap: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Règles communes</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Dites une fois, au-dessus des points d’entrée, à qui les lit — quelle que soit la matière.</div>
        {lecture.reglesCommunes.map((x) => (
          <div key={x.id} style={{ display: 'grid', gap: 4, padding: 8, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={x.texte} disabled={!estRq} aria-label="Texte de la règle commune" placeholder="Texte affiché"
                onChange={(e) => changerRegle(x.id, { texte: e.currentTarget.value })} style={{ ...inputStyle, flex: 1, fontSize: 12.5 }} />
              {estRq && (
                <Bouton taille="sm" type="button" title="Retirer cette règle" onClick={() => changer({ ...lecture, reglesCommunes: lecture.reglesCommunes.filter((y) => y.id !== x.id) })}>−</Bouton>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12 }}>
              {LECTEURS_CATALOGUE.map((l) => (
                <label key={l.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" disabled={!estRq} checked={x.lecteurs.includes(l.code)}
                    onChange={() => changerRegle(x.id, { lecteurs: x.lecteurs.includes(l.code) ? x.lecteurs.filter((y) => y !== l.code) : [...x.lecteurs, l.code] })} />
                  {l.court}
                </label>
              ))}
            </div>
          </div>
        ))}
        {estRq && <Bouton taille="sm" type="button" onClick={ajouterRegle} style={{ justifySelf: 'start' }}>+ Ajouter une règle commune</Bouton>}
      </div>

      {estRq && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Bouton variante="primaire" taille="sm" type="button" disabled={envoi || (!modifie && enregistre)} onClick={enregistrer}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      )}
    </div>
  );
}
